"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const client_auth_1 = require("@deliverr/client-auth");
const lodash_1 = require("lodash");
const core_auth_1 = require("@deliverr/core-auth");
const commons_utils_1 = require("@deliverr/commons-utils");
const commons_logs_1 = require("@deliverr/commons-logs");
const Shared_1 = require("../../../app/src/shared/Shared");
const errors_1 = require("@deliverr/errors");
const PRODUCT_CLIENT_BATCH_LIMIT = (0, commons_utils_1.getEnvNumber)("PRODUCT_CLIENT_BATCH_LIMIT", 100);
const PRODUCT_DSKU_COUNT_LIMIT = (0, commons_utils_1.getEnvNumber)("PRODUCT_DSKU_COUNT_LIMIT", 3000);
const PRODUCT_API_CONCURRENCY_LIMIT = (0, commons_utils_1.getEnvNumber)("PRODUCT_API_CONCURRENCY_LIMIT", 10);
/**
 * ProductClient does not use a version path in its `baseURL`. The class contains
 * v1 and v2 methods and all new endpoints being added to product-service are v2.
 * <p>
 * V2 methods should implement the ApiResponse pattern, i.e. getBrandedPackaging
 */
class ProductClient extends base_client_1.ApiClient {
    constructor(config) {
        const baseURL = config.baseURL ?? process.env.PRODUCT_SERVICE_URL;
        if (!baseURL) {
            throw new Error("PRODUCT_SERVICE_URL environment variable must be set");
        }
        let authEngine = core_auth_1.AuthEngine.COGNITO;
        let tokenRefreshFn = config.tokenRefreshFn;
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            authEngine = core_auth_1.AuthEngine.COGNITO_INTERNAL;
            tokenRefreshFn = tokenRefreshFn ?? new client_auth_1.OAuthClientTokenBuilder(config).getTokenFn();
        }
        super({ ...config, baseURL, authEngine, tokenRefreshFn });
    }
    async getProduct(dsku, options) {
        return this.get("/v1/manager/product", {
            dsku,
            ...options,
        });
    }
    async getProductAuth0(dsku, options) {
        return this.get("/v1/manager/productAuth0", {
            dsku,
            ...options,
        });
    }
    /**
     * Set `useRequestBody` to true if fetching a large number of products (1000s)
     * at a time to prevent a 421, URI too long.
     */
    async getProducts(dskus, options, useRequestBody = false) {
        const ctx = { fn: "getProducts", dskus, useRequestBody };
        commons_logs_1.log.info(ctx, "start");
        if (dskus.length > PRODUCT_DSKU_COUNT_LIMIT) {
            const errorMessage = `dsku count exceeds the limit of ${PRODUCT_DSKU_COUNT_LIMIT}. Provided: ${dskus.length}, Limit: ${PRODUCT_DSKU_COUNT_LIMIT}`;
            commons_logs_1.log.error({ errorMessage, dskuCount: dskus.length, limit: PRODUCT_DSKU_COUNT_LIMIT });
            throw new errors_1.DeliverrError({
                code: Shared_1.ProductError.PRODUCT_DSKU_LIMIT_EXCEEDED,
                message: errorMessage,
            });
        }
        if ((0, lodash_1.isEmpty)(dskus)) {
            commons_logs_1.log.info(ctx, "No DSKUs provided, returning empty result.");
            return {};
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        commons_logs_1.log.info(ctx, `Processing ${dskuChunks.length} batches of up to ${PRODUCT_CLIENT_BATCH_LIMIT} DSKUs each.`);
        const presignedUrls = dskuChunks.map((dskuChunk, index) => async () => {
            const { batchIndex, batchSize } = { batchIndex: index, batchSize: dskuChunk.length };
            commons_logs_1.log.info({ ...ctx, batchIndex, batchSize }, `Processing batch ${batchIndex + 1}/${dskuChunks.length}.`);
            if (useRequestBody) {
                const { url, data } = {
                    url: "/v1/manager/products",
                    data: {
                        dskus: dskuChunk,
                        ...options,
                    },
                };
                const response = await this.axios.request({
                    url,
                    data,
                });
                commons_logs_1.log.info({ ...ctx, batchIndex }, `Batch ${batchIndex + 1} completed successfully.`);
                return response.data;
            }
            return this.getBatch("/v1/manager/products", {
                dskus: dskuChunk,
                ...options,
            });
        });
        try {
            const results = await Promise.all(commons_utils_1.PromiseLimiter.executeFunctions(presignedUrls, PRODUCT_API_CONCURRENCY_LIMIT));
            return (0, lodash_1.merge)({}, ...results);
        }
        catch (error) {
            const { err } = { err: error };
            commons_logs_1.log.error({ ...ctx, err }, "Error fetching products");
            throw error;
        }
    }
    async getProductsBySellerId(sellerId, limit = 20, offset = 0, options) {
        return this.getBatch(`/v1/manager/products/${sellerId}`, {
            limit,
            offset,
            ...options,
        });
    }
    async getProductsBySellerIdAndMsku(sellerId, msku, options) {
        return this.getBatch(`/v1/products/sellerId/${sellerId}`, {
            msku,
            ...options,
        });
    }
    async getProductsBySellerIdAndProductAliasAttributes(sellerId, channelProductId, channelId, sellerListingSolutionUUID, includeProductAliases) {
        return this.get(`/v1/product/sellerId/${sellerId}`, {
            channelProductId,
            channelId,
            sellerListingSolutionUUID,
            includeProductAliases,
        });
    }
    async getFefoEnabledSkusForSeller(sellerId) {
        return this.get(`/v1/fefoEnabledSkus/${sellerId}`);
    }
    async getFefoEnabledSkusForSellerV2(sellerId) {
        return this.get(`/v2/fefo-enabled-skus/${sellerId}`);
    }
    async getSellerIdsBy(body) {
        return this.post(`/v1/sellers/query`, body);
    }
    /**
     * This endpoint should be used solely in integration tests.
     */
    async createProductBoxDimensionsForTesting(productBoxDimensions) {
        return this.post("/v1/manager/box-dimensions", productBoxDimensions);
    }
    async updateProductDimensions(productDimensionsRequest) {
        return this.put("/v1/dimensions", productDimensionsRequest);
    }
    /**
     * This endpoint creates product case pack for a dsku.
     */
    async createProductCasePack(productCasePack) {
        return this.post("/v1/manager/case-pack", productCasePack);
    }
    /**
     * This endpoint creates product multiple products case pack.
     */
    async createProductsCasePack(productsCasePack) {
        return this.post("/v1/manager/case-packs", productsCasePack);
    }
    /**
     * This endpoint updates product case pack by using case pack dsku.
     */
    async updateProductCasePackByDsku(dsku, productCasePackData) {
        return this.put(`/v1/manager/case-pack/${dsku}`, productCasePackData);
    }
    /**
     * This endpoint should be used solely for:
     * - Manual creation via sellerportal
     * - Fulfillment by Wish (FBW)
     */
    async createProduct(product) {
        return this.post("/v1/product", product);
    }
    /*
     * Creates products in bulk
     */
    async createProducts(products) {
        return this.post("/v1/products", products);
    }
    async createProductV2(product) {
        return this.post("/v2/manager/product", product);
    }
    /*
     * Creates products in batch using batch CreateProductV2 endpoint
     */
    async createProductsV2(products) {
        return this.post("/v2/manager/products", products);
    }
    async bulkCreateProducts(bulkCreateRequest) {
        const formData = new FormData();
        formData.append("file", bulkCreateRequest.file);
        formData.append("sellerId", bulkCreateRequest.sellerId);
        formData.append("logisticsAccountId", bulkCreateRequest.logisticsAccountId);
        return this.post("/v2/manager/bulk-create-products", formData);
    }
    async bulkUpdateProducts(bulkUpdateRequest) {
        const formData = new FormData();
        formData.append("file", bulkUpdateRequest.file);
        formData.append("sellerId", bulkUpdateRequest.sellerId);
        return this.post("/v2/manager/bulk-update-products", formData);
    }
    async update(product) {
        return this.put(`/v1/product/${product.dsku}`, product);
    }
    async createProductOrUpdateAlias(product) {
        return this.put("/v1/product", product);
    }
    async createProductsOrUpdateAliases(products) {
        return this.put("/v1/workflow/products", products);
    }
    async linkProductAlias(channelId, channelProductId, sellerId, dskuToLink, sellerListingSolutionUUID) {
        try {
            return await this.put("/v1/product-alias/link", {
                channelId,
                channelProductId,
                sellerId,
                sellerListingSolutionUUID,
                dskuToLink,
            });
        }
        catch (err) {
            this.handleError("linkProductAlias", err.response, {
                channelId,
                channelProductId,
                sellerId,
                dskuToLink,
            });
        }
    }
    async unlinkProductAlias(channelId, channelProductId, sellerId, sellerListingSolutionUUID) {
        try {
            return await this.put("/v1/product-alias/unlink", {
                channelId,
                channelProductId,
                sellerId,
                sellerListingSolutionUUID,
            });
        }
        catch (err) {
            this.handleError("unlinkProductAlias", err.response, {
                channelId,
                channelProductId,
                sellerId,
            });
        }
    }
    async getProductAlias(channelProductId, sellerListingSolutionUUID, sellerId, channelId) {
        return this.get("/v2/manager/product-alias", {
            channelProductId,
            sellerListingSolutionUUID,
            sellerId,
            channelId,
        });
    }
    async getProductAliasesBySlsUUID(sellerListingSolutionUUID) {
        return this.get("/v2/manager/product-aliases", {
            sellerListingSolutionUUID,
        });
    }
    async validateBarcodes(barcodes) {
        return this.post("/v1/barcode/validate", barcodes);
    }
    async isApprovedForSale(dsku) {
        try {
            await this.post(`/v1/product/validate/${dsku}`, undefined);
            return true;
        }
        catch (err) {
            if ([400, 404].includes(err.response.status)) {
                return false;
            }
            this.handleError("isApprovedForSale", err.response, { dsku });
            return undefined;
        }
    }
    async batchIsApprovedForSale(dskus) {
        return this.get("/v1/products/validate", {
            dskus: dskus.join(","),
        });
    }
    async getShippingSpecificationBatch(dskus, useRequestBody = false) {
        if (useRequestBody) {
            return this.axios
                .request({
                url: "/v1/shipping-specification/",
                data: {
                    dskus,
                },
            })
                .then((res) => res.data);
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v1/shipping-specification/", {
            dskus: dskuChunk,
        })))));
    }
    async createCustomsInformation(customsInformation) {
        return this.post("/v1/manager/customs-information", customsInformation);
    }
    async updateCustomsInformation(dsku, customsInformationUpdate) {
        return this.put(`/v1/manager/customs-information/${dsku}`, customsInformationUpdate);
    }
    async createBarcodes(dskuBarcodesMap) {
        return this.post("/v1/barcode", dskuBarcodesMap);
    }
    // TODO: Remove this code after migration(Cohort 5)
    async createCustomBarcodes(dskuBarcodesMap) {
        return this.post("/v2/manager/customBarcode", dskuBarcodesMap);
    }
    async createOrUpdateHazmatInformation(hazmatInformation) {
        return this.put("/v1/manager/hazmat", hazmatInformation);
    }
    async createOrUpdateShippingSpecification(shippingSpecification) {
        return this.put("/v1/shipping-specification", {
            [shippingSpecification.dsku]: shippingSpecification,
        });
    }
    async createOrUpdateProductPreparation(productPreparation) {
        return this.put("/v1/manager/product-preparation", productPreparation);
    }
    /**
     * Virtual Bundle endpoints
     * - Virtual Bundles are not physical products, but a recipe defined by a list of DSKU & qty pairs
     * - ProductAliases can be linked to Bundles by using the `bsku` bundle identifier on the `dsku` field
     */
    async createBundle(newBundle, fromDsku) {
        return this.post("/v2/manager/bundles", newBundle, {
            dsku: fromDsku,
        });
    }
    async getBundleByAlias(channelBundleId, sellerListingSolutionUUID, sellerId, channelId, bundleDetailsOptions) {
        return this.get(`/v2/bundles/bundle-alias`, {
            channelBundleId,
            sellerListingSolutionUUID,
            sellerId,
            channelId,
            ...bundleDetailsOptions,
        });
    }
    async getBundlesBySellerId(sellerId, options) {
        return this.get(`/v2/manager/bundles/sellerId/${sellerId}`, options);
    }
    async getBundle(bsku, options) {
        return this.get(`/v2/bundles/${bsku}`, options);
    }
    async getBundlesByDsku(dsku) {
        return this.get(`/v2/bundles/dsku/${dsku}`);
    }
    async updateBundle(bsku, updates) {
        return this.put(`/v2/bundles/${bsku}`, updates);
    }
    /**
     * Branded Packaging endpoints.
     * These branded packaging meterials (poly bag, box, gift wrap, etc) are stored in the BrandedPackaging table in the product-service.
     */
    async createBrandedPackaging(brandedPackagingData) {
        return this.post(`/v2/packaging/`, brandedPackagingData);
    }
    async getBrandedPackaging(dsku, options) {
        return this.get(`/v2/packaging/${dsku}`, options);
    }
    async getAllBrandedPackaging(dskus) {
        return this.get(`/v2/packaging/`, dskus);
    }
    async getAllBrandedPackagingBySellerId(options) {
        return this.get(`/v2/packaging/`, options);
    }
    async deleteBrandedPackaging(dsku) {
        return this.delete(`/v2/packaging/${dsku}`);
    }
    async searchBrandedPackaging(dskus) {
        return (await this.axios.request({
            method: "GET",
            url: "/v2/packaging/search/",
            data: {
                dskus,
            },
        })).data;
    }
    async getUnifiedProducts(dskus, options, useRequestBody) {
        /**
         * WARNING: useRequestBody will not work on browsers and should be avoided.
         * This has been added to facilitate swapping getProducts, which currently uses this on some services
         */
        if (useRequestBody) {
            return this.axios
                .request({
                url: "/v2/unified-products",
                data: {
                    dskus,
                    ...options,
                },
            })
                .then((res) => res.data);
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v2/unified-products", {
            dskus: dskuChunk,
            ...options,
        })))));
    }
    /**
     * Takes an existing dsku and saves the kitting information for that dsku in the database.
     */
    async annotateProductAsKit(kitCreationRequest) {
        return this.post("/v1/manager/kit", kitCreationRequest);
    }
    async getProductCasePacks(dskus, options) {
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v1/manager/case-packs", {
            dskus: dskuChunk,
            ...options,
        })))));
    }
    async createOrUpdateProductCategory(productCategory) {
        return this.put("/v1/product-taxonomy-node", productCategory);
    }
    async syncWarehouseItem(request) {
        return this.post("/v1/warehouse-item", request);
    }
    async getWarehouseItemsForSku(dsku, synced = true) {
        return this.get(`/v1/warehouse-item/${dsku}`, { synced });
    }
    async getWarehouseItem(warehouseId, dsku) {
        return this.get(`/v1/warehouse-item/${dsku}/warehouse/${warehouseId}`).catch((error) => {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                default:
                    throw error;
            }
        });
    }
    async reduceBdskusToSkuPairs(bdskus) {
        return this.get(`/v2/manager/products/sku-pairs`, { bdskus });
    }
    async updateProductAliasEligibility(channelId, dskus, eligibility) {
        try {
            return await this.put(`/v1/product-alias/eligibility`, { channelId, dskus, eligibility });
        }
        catch (err) {
            this.handleError("eligibility", err.response, {
                channelId,
                dskus: dskus.join(","),
                eligibility: eligibility.toString(),
            });
        }
    }
    async uploadKitInstructions(dsku, files, textInstruction) {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append("instructions", file);
        });
        if (textInstruction) {
            formData.append("textInstruction", textInstruction);
        }
        return this.post(`/v1/kit/instructions/${dsku}`, formData);
    }
    async getKitInstructions(dsku) {
        return this.get(`/v1/kit/instructions/${dsku}`);
    }
    async getBulkDsku(sellerId, mskus) {
        return this.axios
            .request({
            method: "GET",
            url: `/v2/dskus/${sellerId}`,
            data: {
                mskus,
            },
        })
            .then((res) => res.data);
    }
    handleError(name, errorResponse, logData) {
        if (errorResponse.status === 504) {
            commons_logs_1.log.error("NETWORK_ERR");
            throw new Error("NETWORK_ERR");
        }
        else if (errorResponse.status === 404) {
            commons_logs_1.log.error("NOT_FOUND");
            throw new Error("NOT_FOUND");
        }
        else {
            commons_logs_1.log.error(name, { ...errorResponse.data, ...logData });
            throw errorResponse.data;
        }
    }
}
exports.ProductClient = ProductClient;
//# sourceMappingURL=ProductClient.js.map